import type { SalaryClassEchelon, SalaryClassNiveau } from '../types/firestore'

// Extrait de lib/firestore/mutations.ts (type porté, source Firestore non portée — frags modules)
export type SalaryClassInput = {
  id: string
  label: string
  description: string | null
  sort_order: number
  aug_grille_pct: number | null
  base_hours_per_week: number
  weeks_per_year: number
  echelons: SalaryClassEchelon[]
  niveaux: SalaryClassNiveau[]
}

// Extrait de components/salary/real-payroll-table.tsx (type porté, source React non portée — frags modules)
export type RealPayrollRow = {
  email: string
  name: string
  // Employé actif : un inactif reste affiché (badge) mais est exclu du total.
  active: boolean
  // ID d'équipe (pulse_teams) — clé stable pour les cibles par équipe
  // (insensible au renommage) ; null = sans équipe.
  teamId: string | null
  teamName: string | null
  classLabel: string | null
  // Échelon de paie — null si non positionné OU hors grille (échelon périmé).
  echelon: number | null
  // Hors grille : la paie suit le taux réel ; direction dérivée du taux vs la
  // plage du niveau ('above' = HG+, 'below' = HG−), référence = plafond/plancher.
  offGrid: boolean
  offGridDirection: 'above' | 'below' | 'in_range' | null
  offGridReferenceEchelon: number | null
  // Taux grille de l'année (classe/échelon) — null si non positionné. Pour un
  // hors grille : taux de RÉFÉRENCE (plafond du niveau/plancher), l'écart se lit
  // alors « vs où il devrait plafonner ».
  gridRate: number | null
  // Taux réel en vigueur en fin de période couverte — null si aucun.
  realRate: number | null
  gapDelta: number | null
  gapPct: number | null
  // Masse salariale réelle prorata temporis de l'année (salaire de base).
  annual: number
  // Salaire annuel estimé à la grille (prorata) — repli quand aucun taux réel
  // daté n'existe. Sert au calcul de la part de masse ; null si non applicable.
  gridAnnual: number | null
  // Supplément des périodes d'heures spéciales (temps et demi >40 h).
  specialHours: number
  // Supplément des primes ponctuelles ($/h sur une période).
  premiums: number
  // Avantages / coûts employeur annuels (assurance, cellulaire, allocation, véhicule).
  benefits: number
  // Véhicule attribué mais aucun coût d'année configuré : compté 0 $, à signaler.
  vehicleUnpriced: boolean
  // Boni réel ($) = salaire annuel réel × % atteint. Inclus dans le coût total.
  bonus: number
  bonusTargetPct: number | null // % possible d'atteinte (fraction)
  bonusActualPct: number | null // % réel atteint (fraction)
  coverageDays: number
  unpriced: boolean
}

// Extrait de components/salary/real-payroll-table.tsx (fonction pure portée, source React non portée — frags modules)
// Coût réel d'une ligne = salaire de base (estimation grille en repli si non
// tarifé) + heures spéciales + primes + avantages + boni. Source unique
// réutilisée par le pied de tableau ET la carte de total de la page (évite la
// dérive entre les deux). Même convention que effectiveBaseSalary.
export function realPayrollRowCost(r: RealPayrollRow): number {
  const base = r.unpriced ? (r.gridAnnual ?? 0) : r.annual
  return base + r.specialHours + r.premiums + r.benefits + r.bonus
}
